import { of } from 'rxjs'
import { catchError, filter, switchMap } from 'rxjs/operators'

import { ChangeDetectorRef, Component, EventEmitter, OnInit, Output } from '@angular/core'
import { NavigationEnd, Router } from '@angular/router'
import { AdminService } from '@services/admin.service'
import { AuthenticationService } from '@services/authentication.service'
import { PermissionService } from '@services/permission.service'
import { SubscriptionService } from '@services/subscription.service'
import { UserService } from '@services/user.service'

import { APP_ADMIN_NAV_ITEM, APP_NAV_ITEMS, NavItem } from '../app-navigation'

@Component({
    selector: 'shared-navbar-menu-mobile',
    templateUrl: './navbar-menu-mobile.component.html',
    styleUrls: ['./navbar-menu-mobile.component.css'],
  standalone: false
})
export class NavbarMenuMobileComponent implements OnInit {
    @Output() menuClosed = new EventEmitter<void>()

    get currentModule() { return this._authenticationService.currentModule }
    get hasUserAdminAccess() { return this._authenticationService.hasUserAdminAccess }
    get inFreeTier() { return this._subscriptionService.inFreeTier }
    get isLegacy() { return this._subscriptionService.isLegacy }
    get isSuperAdmin() { return this._userService.currentUser?.superAdmin ?? false }
    get showAdminLink() { return this.isSuperAdmin }

    adminNavItem = APP_ADMIN_NAV_ITEM
    isOnAdminPage = false
    navItems: NavItem[] = APP_NAV_ITEMS

    constructor(
        private _adminService: AdminService,
        private _authenticationService: AuthenticationService,
        private _cdr: ChangeDetectorRef,
        private _permissionService: PermissionService,
        private _router: Router,
        private _subscriptionService: SubscriptionService,
        private _userService: UserService,
    ) { }

    ngOnInit(): void {
        this._authenticationService.isAuthenticated$.pipe(
            filter(isAuth => isAuth),
            switchMap(() => this._adminService.getCurrentOrgSettings().pipe(
                catchError(() => of(null))
            )),
            switchMap(() => this._authenticationService.getCurrentUsersRoles$().pipe(
                catchError(() => of([]))
            )),
            switchMap(() => this._permissionService.refresh().pipe(
                catchError(() => of(null))
            ))
        ).subscribe()

        this._adminService.currentOrgSettings$.subscribe(() => {
            this._cdr.detectChanges()
        })

        this._permissionService.roles$.subscribe(() => {
            this._cdr.detectChanges()
        })

        this.isOnAdminPage = this._router.url.startsWith('/admin')
        this._router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe((event: NavigationEnd) => {
            this.isOnAdminPage = event.urlAfterRedirects.startsWith('/admin')
            this._cdr.detectChanges()
        })
    }

    navigate(item: NavItem): void {
        if (this.isNavItemDisabled(item) || this.isNavItemHiddenByOrgSettings(item)) {
            return
        }
        this._router.navigate([item.route])
        this.menuClosed.emit()
    }

    navigateAdmin(): void {
        this._router.navigate([this.adminNavItem.route])
        this.menuClosed.emit()
    }

    isNavItemActive(item: NavItem): boolean {
        return this.currentModule === item.module
    }

    isNavItemDisabled(item: NavItem): boolean {
        if (item.disabledInFreeTier && this.inFreeTier) {
            return true
        }
        return false
    }

    shouldShowNavItem(item: NavItem): boolean {
        if (item.hideForLegacy && this.isLegacy) {
            return false
        }
        if (item.hideInFreeTier && this.inFreeTier) {
            return false
        }
        if (item.requiresAdmin && !this.hasUserAdminAccess && !this.inFreeTier && !this.isSuperAdmin) {
            return false
        }
        if (this._adminService.isModuleHidden(item.moduleId)) {
            return this.isOnAdminPage
        }
        if (!this.isOnAdminPage && !this._permissionService.canAccess(item.module)) {
            return false
        }
        return true
    }

    isNavItemHiddenByOrgSettings(item: NavItem): boolean {
        return this._adminService.isModuleHidden(item.moduleId)
    }
}
